import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {GoogleService} from '../api';
import type {Location, PlaceAutocomplete, PlaceIdDetail} from '../api/types';
import {addLocation} from '../store';
import {MyWeather} from '../utils';
import useApiClient, {isThrottled, throttledAlert} from './useApiClient';

/**
 * A place picked in My weather's search (FE #381), in both apps: its
 * details are asked for (coordinates, timezone), it joins the device's
 * places (Recent on mobile; where the webapp's place page finds it), and
 * `onOpen` opens it.
 *
 * The details can answer after the user picked something else or the
 * results went away: only the latest pick acts, and only while they show.
 * A superseded place is not kept either: the user moved on from it.
 * `supersede` is for a pick of another kind (a station). A failure is the
 * app's error snackbar, from the API client; a 429, which raises none there
 * (FE #404), is said here, once, for the latest pick only (FE #427).
 */
const useMyWeatherPlacePick = (onOpen: (place: Location) => void) => {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();
  const {fetch} = useApiClient();
  const picks = React.useRef(0);
  const onOpenRef = React.useRef(onOpen);
  React.useLayoutEffect(() => {
    onOpenRef.current = onOpen;
  });
  React.useEffect(
    () => () => {
      picks.current += 1;
    },
    [],
  );

  const pickPlace = React.useCallback(
    (pick: PlaceAutocomplete) => {
      const latest = ++picks.current;
      fetch(GoogleService.placeId(pick.place_id))
        .then((detail: PlaceIdDetail) => {
          if (latest !== picks.current) {
            return;
          }
          const place = MyWeather.placeFromPick(detail, pick);
          dispatch(addLocation(place));
          onOpenRef.current(place);
        })
        .catch((error: unknown) => {
          if (latest === picks.current && isThrottled(error)) {
            dispatch(throttledAlert(formatMessage));
          }
        });
    },
    [dispatch, fetch, formatMessage],
  );

  const supersede = React.useCallback(() => {
    picks.current += 1;
  }, []);

  return {pickPlace, supersede};
};

export default useMyWeatherPlacePick;
